import React from 'react';
import { Plus, Trash2, ChevronDown, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import TagInput from '../TagInput';

const FIELD_OPTIONS = [
    { group: 'Contact', fields: [
        { key: 'contact.tags', label: 'Tags', type: 'array' },
        { key: 'contact.industry', label: 'Industry', type: 'string' },
        { key: 'contact.title', label: 'Job Title', type: 'string' },
        { key: 'contact.seniority', label: 'Seniority', type: 'string' },
        { key: 'contact.email', label: 'Email', type: 'string' },
        { key: 'contact.disc_profile', label: 'DISC Profile', type: 'string' },
        { key: 'contact.engagement_score', label: 'Engagement Score', type: 'number' },
        { key: 'contact.relationship_strength', label: 'Relationship', type: 'string' },
        { key: 'contact.created_at', label: 'Created Date', type: 'date' },
        { key: 'contact.last_tagged_at', label: 'Last Tagged Date', type: 'date' },
        { key: 'contact.last_form_completed_at', label: 'Form Completion Date', type: 'date' },
    ]},
    { group: 'Company', fields: [
        { key: 'account.industry', label: 'Company Industry', type: 'string' },
        { key: 'account.size', label: 'Company Size', type: 'string' },
        { key: 'account.annual_revenue', label: 'Revenue', type: 'number' },
        { key: 'account.status', label: 'Company Status', type: 'string' },
        { key: 'account.employee_range', label: 'Employee Range', type: 'string' },
    ]},
    { group: 'Deal', fields: [
        { key: 'deal.stage', label: 'Deal Stage', type: 'string' },
        { key: 'deal.value', label: 'Deal Value', type: 'number' },
        { key: 'deal.probability', label: 'Probability', type: 'number' },
    ]},
    { group: 'Enrollment', fields: [
        { key: 'enrollment.enrolled_at', label: 'Enrolled Date', type: 'date' },
        { key: 'enrollment.emails_sent', label: 'Emails Sent', type: 'number' },
        { key: 'enrollment.emails_opened', label: 'Emails Opened', type: 'number' },
    ]},
    { group: 'Form', fields: [
        { key: 'form_submission.created_at', label: 'Last Submission Date', type: 'date' },
        { key: 'form_submission.form_name', label: 'Form Name', type: 'string' },
    ]},
    { group: 'Event', fields: [
        { key: 'event.tag', label: 'Event Tag', type: 'string' },
        { key: 'event.tagged_at', label: 'Event Tag Date', type: 'date' },
        { key: 'event.old_stage', label: 'Previous Stage', type: 'string' },
        { key: 'event.new_stage', label: 'New Stage', type: 'string' },
    ]},
];

const STRING_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'];
const NUMBER_OPERATORS = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal'];
const ARRAY_OPERATORS = ['contains', 'not_contains', 'is_empty', 'is_not_empty'];
// BUG-048 — explicit inclusive boundary operators. 'before'/'after' stay
// strict (exclusive); 'on_or_before'/'on_or_after' include the boundary day.
const DATE_OPERATORS = ['days_since_gt', 'days_since_lt', 'days_until_gt', 'days_until_lt', 'before', 'on_or_before', 'after', 'on_or_after', 'between', 'on', 'is_empty', 'is_not_empty'];

const OPERATOR_LABELS = {
    'days_since_gt': 'more than N days ago',
    'days_since_lt': 'less than N days ago',
    'days_until_gt': 'more than N days from now',
    'days_until_lt': 'less than N days from now',
    'before': 'is strictly before date',
    'on_or_before': 'is on or before date',
    'after': 'is strictly after date',
    'on_or_after': 'is on or after date',
    'between': 'is between dates',
    'on': 'is on date',
    'is_empty': 'has no date',
    'is_not_empty': 'has a date',
};

function getOperators(fieldType) {
    switch (fieldType) {
        case 'number': return NUMBER_OPERATORS;
        case 'array': return ARRAY_OPERATORS;
        case 'date': return DATE_OPERATORS;
        default: return STRING_OPERATORS;
    }
}

function getFieldDescriptor(fieldKey, options) {
    for (const group of (options || FIELD_OPTIONS)) {
        const field = (group.fields || []).find(f => f.key === fieldKey);
        if (field) return field;
    }
    return null;
}

function getFieldType(fieldKey, options) {
    const f = getFieldDescriptor(fieldKey, options);
    return f ? f.type : 'string';
}

const INPUT = "w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-stone-400";

function getDefaultOperator(fieldType) {
    switch (fieldType) {
        case 'date': return 'days_since_lt';
        case 'number': return 'equals';
        case 'array': return 'contains';
        default: return 'equals';
    }
}

export default function ConditionBuilder({ value, onChange, campaignTimezone, fields }) {
    const group = value || { logic: 'and', conditions: [] };
    // Optional field list (e.g. Form Flow prior-question fields). Falls back to
    // the hardcoded contact/account/deal schema so existing callers are unaffected.
    const fieldOptions = fields || FIELD_OPTIONS;

    const updateGroup = (updates) => onChange({ ...group, ...updates });

    const addCondition = () => {
        updateGroup({ conditions: [...(group.conditions || []), { field: '', operator: 'equals', value: '' }] });
    };

    const updateCondition = (index, updates) => {
        const conds = [...(group.conditions || [])];
        conds[index] = { ...conds[index], ...updates };
        // When switching to 'between', ensure value2 is initialized
        if (updates.operator === 'between' && !conds[index].value2) {
            conds[index].value2 = '';
        }
        updateGroup({ conditions: conds });
    };

    const removeCondition = (index) => {
        updateGroup({ conditions: (group.conditions || []).filter((_, i) => i !== index) });
    };

    return (
        <div className="space-y-2">
            {/* Logic toggle */}
            <div className="flex items-center gap-2">
                <span className="text-xs text-stone-500">Match</span>
                <button type="button" onClick={() => updateGroup({ logic: group.logic === 'and' ? 'or' : 'and' })}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded ${group.logic === 'and' ? 'bg-stone-900 text-white' : 'bg-amber-500 text-white'}`}>
                    {group.logic === 'and' ? 'ALL' : 'ANY'}
                </button>
                <span className="text-xs text-stone-500">of these conditions</span>
            </div>

            {/* Conditions */}
            {(group.conditions || []).map((cond, i) => {
                const fieldType = getFieldType(cond.field, fieldOptions);
                const fieldDesc = getFieldDescriptor(cond.field, fieldOptions);
                const operators = getOperators(fieldType);
                return (
                    <div key={i} className="bg-white rounded-lg border border-stone-200 p-2 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                            {/* Field */}
                            <select value={cond.field || ''} onChange={e => {
                                    const newField = e.target.value;
                                    const newType = getFieldType(newField, fieldOptions);
                                    updateCondition(i, { field: newField, operator: getDefaultOperator(newType), value: '', value2: undefined });
                                }}
                                className={`${INPUT} flex-1 min-w-0`}>
                                <option value="">Select field...</option>
                                {fieldOptions.map(g => (
                                    <optgroup key={g.group} label={g.group}>
                                        {g.fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>

                            {/* Delete */}
                            <button type="button" onClick={() => removeCondition(i)} className="p-1 text-stone-400 hover:text-red-500 flex-shrink-0">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Operator */}
                        <select value={cond.operator || 'equals'} onChange={e => updateCondition(i, { operator: e.target.value })}
                            className={INPUT}>
                            {operators.map(op => <option key={op} value={op}>{OPERATOR_LABELS[op] || op.replace(/_/g, ' ')}</option>)}
                        </select>

                        {/* Value */}
                        {!['is_empty', 'is_not_empty', 'exists', 'not_exists'].includes(cond.operator) && (
                            (fieldDesc?.options && ['equals', 'not_equals', 'in_list', 'not_in_list'].includes(cond.operator)) ? (
                                <select value={cond.value ?? ''} onChange={e => updateCondition(i, { value: e.target.value })} className={`${INPUT} flex-1`}>
                                    <option value="">Select value...</option>
                                    {fieldDesc.options.map(o => {
                                        const ov = typeof o === 'string' ? o : o.value;
                                        const ol = typeof o === 'string' ? o : (o.label ?? String(o.value));
                                        return <option key={String(ov)} value={ov}>{ol}</option>;
                                    })}
                                </select>
                            ) : fieldType === 'array' ? (
                                <div className="flex-1 min-w-[140px]">
                                    <TagInput
                                        singleSelect
                                        singleValue={cond.value || ''}
                                        onSingleChange={value => updateCondition(i, { value })}
                                        placeholder="Search tags..."
                                    />
                                </div>
                            ) : cond.operator === 'between' ? (
                                <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-stone-400 w-8 flex-shrink-0">From</span>
                                        <input type="date" value={cond.value || ''} onChange={e => updateCondition(i, { value: e.target.value })}
                                            className={`${INPUT} flex-1 min-w-0`} />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-stone-400 w-8 flex-shrink-0">To</span>
                                        <input type="date" value={cond.value2 || ''} onChange={e => updateCondition(i, { value2: e.target.value })}
                                            className={`${INPUT} flex-1 min-w-0`} />
                                    </div>
                                </div>
                            ) : ['before', 'after', 'on', 'on_or_before', 'on_or_after'].includes(cond.operator) ? (
                                <input type="date" value={cond.value || ''} onChange={e => updateCondition(i, { value: e.target.value })}
                                    className={`${INPUT} flex-1 min-w-0`} />
                            ) : (
                                <input value={cond.value || ''} onChange={e => updateCondition(i, { value: e.target.value })}
                                    type={fieldType === 'number' || ['days_since_gt', 'days_since_lt', 'days_until_gt', 'days_until_lt'].includes(cond.operator) ? 'number' : 'text'}
                                    placeholder={['days_since_gt', 'days_since_lt', 'days_until_gt', 'days_until_lt'].includes(cond.operator) ? 'Days...' : 'Value...'}
                                    className={`${INPUT} flex-1`} />
                            )
                        )}

                        {/* Timezone context for date conditions */}
                        {fieldType === 'date' && ['before', 'after', 'between', 'on', 'on_or_before', 'on_or_after'].includes(cond.operator) && campaignTimezone && (
                            <div className="flex items-center gap-1 text-[10px] text-stone-400">
                                <Globe className="w-3 h-3" />
                                <span>Dates evaluated in <strong className="text-stone-500">{campaignTimezone}</strong></span>
                            </div>
                        )}
                    </div>
                );
            })}

            <button type="button" onClick={addCondition}
                className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 px-2 py-1">
                <Plus className="w-3 h-3" /> Add condition
            </button>
        </div>
    );
}
